import { useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Swords, Flame, Anchor, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CinematicVideo } from "@/components/CinematicVideo";
import { DialecticCore } from "@/components/DialecticCore";
import { ToolSpecs } from "@/components/ToolSpecs";
import { ToolCounters } from "@/components/ToolCounters";

const DUEL_VIDEO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/e2tpyd31_gemini_generated_video_3f9379d1.mp4";

const DUEL_SPECS = {
  fr: ["2 voies", "Débat dialectique", "Synthèse finale"],
  en: ["2 paths", "Dialectic debate", "Final synthesis"],
  es: ["2 vías", "Debate dialéctico", "Síntesis final"],
  nl: ["2 wegen", "Dialectisch debat", "Eindsynthese"],
};
const DUEL_ICONS = [Anchor, Swords, Sparkles];

export function TeaserDuel() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const d = t("landing.duel", { returnObjects: true });
  const c = d.concept || {};
  const lang = (i18n.language || "fr").slice(0, 2);
  const openTool = () => { localStorage.setItem("coccon_pending_tab", "deuxVoies"); navigate("/atelier"); };

  return (
    <section className="max-w-6xl mx-auto px-6 py-14 sm:py-20" data-testid="duel-teaser-section">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        data-testid="duel-teaser-btn"
        className="group relative block w-full text-left rounded-3xl overflow-hidden glass frame-pulse border border-primary/25"
        style={{ boxShadow: "0 0 40px hsl(32 90% 55% / 0.12)" }}
      >
        <div className="p-4 sm:p-6 pb-0">
          <CinematicVideo src={DUEL_VIDEO} testId="duel-cinematic-video" fade sound />
        </div>
        <div className="p-8 sm:p-12 pt-6 sm:pt-8 flex flex-col sm:flex-row items-start sm:items-center gap-6">
          <span className="w-16 h-16 rounded-2xl bg-primary/12 border border-primary/30 flex items-center justify-center text-primary flex-shrink-0 group-hover:scale-105 transition-transform">
            <Swords size={30} strokeWidth={1.6} />
          </span>
          <div className="flex-1">
            <span className="inline-block text-[0.7rem] uppercase tracking-[0.25em] text-primary/70 border border-primary/25 rounded-full px-3 py-1 mb-3">
              {d.badge}
            </span>
            <h2 className="font-display text-4xl sm:text-5xl text-gradient-orange leading-none mb-3">{d.name}</h2>
            <p className="text-foreground/60 leading-relaxed max-w-xl">{d.tagline}</p>
            <ToolSpecs lang={lang} className="mt-5 w-full sm:max-w-sm"
              items={(DUEL_SPECS[lang] || DUEL_SPECS.fr).map((text, i) => ({ Icon: DUEL_ICONS[i], text }))} />
            <ToolCounters ns="deuxVoies" className="mt-4" />
          </div>
          <button
            onClick={() => setOpen(true)}
            data-testid="duel-cta-btn"
            className="text-sm font-semibold text-primary/90 inline-flex items-center gap-2 whitespace-nowrap self-center border border-primary/30 rounded-full px-5 py-2.5 hover:bg-primary/10 transition-colors"
          >
            <Sparkles size={15} /> {d.cta}
          </button>
        </div>
      </motion.div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="glass border border-primary/25 max-w-2xl rounded-[22px] max-h-[88vh] overflow-y-auto scroll-thin" data-testid="duel-modal" style={{ boxShadow: "0 0 50px hsl(32 90% 55% / 0.15)" }}>
          <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-44 opacity-70" style={{ background: "radial-gradient(120% 100% at 50% 0%, hsl(32 90% 55% / 0.14), transparent 70%)" }} />
          <div className="relative z-10 grid gap-5">
            <DialogHeader className="space-y-2">
              <span className="inline-block w-fit text-[0.62rem] uppercase tracking-[0.28em] text-primary/70 border border-primary/25 rounded-full px-3 py-1" data-testid="duel-modal-eyebrow">{c.eyebrow}</span>
              <DialogTitle className="font-display text-3xl text-gradient-orange flex items-center gap-2.5">
                <Swords size={22} className="text-primary" /> {d.name}
              </DialogTitle>
            </DialogHeader>

            {/* Le Concept */}
            <section data-testid="duel-concept-block">
              <p className="text-[0.7rem] uppercase tracking-[0.25em] text-primary/70 mb-2">{c.cLabel}</p>
              <DialogDescription className="text-foreground/70 leading-relaxed text-[0.95rem]">{c.cBody}</DialogDescription>
            </section>

            {/* Le Duel des Tempéraments */}
            <section data-testid="duel-personas-block">
              <p className="text-[0.7rem] uppercase tracking-[0.25em] text-primary/70 mb-1.5">{c.duelLabel}</p>
              <p className="text-sm text-foreground/55 leading-relaxed mb-4">{c.duelIntro}</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {/* La Voie de l'Ancre — bleu, la thèse */}
                <div className="rounded-2xl border border-sky-300/20 bg-sky-300/[0.05] p-4" data-testid="duel-card-ancre">
                  <p className="font-display text-lg text-sky-300/90 flex items-center gap-2.5 mb-1">
                    <span className="w-9 h-9 rounded-xl bg-sky-300/12 flex items-center justify-center flex-shrink-0"><Anchor size={17} /></span>
                    {c.ancreName}
                  </p>
                  <span className="inline-block text-[0.6rem] uppercase tracking-[0.22em] text-sky-300/60 mb-2.5">{c.ancreTag}</span>
                  <p className="text-sm text-foreground/70 leading-relaxed mb-1.5"><span className="text-sky-300/80 font-medium">{c.roleLabel} — </span>{c.ancreRole}</p>
                  <p className="text-sm text-foreground/60 leading-relaxed"><span className="text-sky-300/80 font-medium">{c.actionLabel} — </span>{c.ancreAction}</p>
                </div>
                {/* La Voie du Feu — ambre, l'antithèse */}
                <div className="rounded-2xl border border-primary/25 bg-primary/[0.06] p-4" data-testid="duel-card-feu">
                  <p className="font-display text-lg text-primary flex items-center gap-2.5 mb-1">
                    <span className="w-9 h-9 rounded-xl bg-primary/15 flex items-center justify-center flex-shrink-0"><Flame size={17} /></span>
                    {c.feuName}
                  </p>
                  <span className="inline-block text-[0.6rem] uppercase tracking-[0.22em] text-primary/60 mb-2.5">{c.feuTag}</span>
                  <p className="text-sm text-foreground/70 leading-relaxed mb-1.5"><span className="text-primary/80 font-medium">{c.roleLabel} — </span>{c.feuRole}</p>
                  <p className="text-sm text-foreground/60 leading-relaxed"><span className="text-primary/80 font-medium">{c.actionLabel} — </span>{c.feuAction}</p>
                </div>
              </div>
              <DialecticCore testId="duel-dialectic-core" label={c.coreLabel} />
            </section>

            {/* La Synthèse */}
            <section className="rounded-2xl border border-primary/20 bg-white/[0.03] p-5" data-testid="duel-synthesis-block">
              <p className="text-[0.7rem] uppercase tracking-[0.25em] text-primary/70 mb-2 flex items-center gap-1.5"><Sparkles size={13} /> {c.synthLabel}</p>
              <p className="font-display italic text-foreground/85 leading-relaxed">{c.synthBody}</p>
            </section>

            <div className="flex justify-center pt-1">
              <button onClick={openTool} data-testid="duel-open-btn" className="btn-frost-orange inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm">
                <Sparkles size={15} /> {d.soon}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
